/**
 * Trajectoire Chart Controller (EPIC-047.4, réécrit EPIC-099.68)
 *
 * Graphe de la page trajectoire (§16.E) rendu avec uPlot — la librairie chart
 * du projet (cf. seuils_dashboard_controller.js). Deux séries **cumulées** :
 *  - Objectif cumulé (trait plein, couleur --accent)
 *  - CA HT réalisé cumulé (trait plein, couleur --info, s'arrête au dernier
 *    mois renseigné — pas de prolongation à 0 sur les mois futurs)
 *
 * Piloté par `setData({ monthlyTarget, monthlyReal, year })` : `monthlyReal`
 * contient 12 valeurs dont `null` pour les mois futurs. Le cumul est calculé
 * ici à partir des séries mensuelles brutes. Le view controller appelle cette
 * méthode après un fetch API réussi.
 *
 * Le graphe n'est qu'une aide visuelle : la table mensuelle en dessous reste
 * l'équivalent accessible (le conteneur porte `role="img"`).
 */

import uPlot from "uplot";

import BaseController from "./base_controller.js";

const MONTH_LABELS = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];

export default class TrajectoireChartController extends BaseController {
  connect() {
    this._data = null;
  }

  setData(data) {
    this._data = data;
    this.render();
  }

  render() {
    if (!this._data) return;
    if (!this.hasContainer()) return;

    const { monthlyTarget = [], monthlyReal = [], year = "" } = this._data;

    const container = this.element;
    const width = container.clientWidth;
    // Pas de layout (jsdom en test, conteneur masqué) → on n'instancie pas uPlot.
    if (!width) return;

    const xs = [];
    const targetCumul = [];
    const realCumul = [];
    let runTarget = 0;
    let runReal = 0;
    let realStopped = false;
    for (let i = 0; i < 12; i += 1) {
      xs.push(i + 1);

      const t = Number(monthlyTarget[i]);
      runTarget += Number.isFinite(t) ? t : 0;
      targetCumul.push(runTarget);

      const r = monthlyReal[i];
      if (realStopped || r === null || r === undefined || !Number.isFinite(Number(r))) {
        // Mois futur (ou première valeur manquante) : la série réalisé
        // s'arrête, pas de point au-delà.
        realStopped = true;
        realCumul.push(null);
      } else {
        runReal += Number(r);
        realCumul.push(runReal);
      }
    }

    const css = getComputedStyle(document.documentElement);
    const token = (name, fallback) => (css.getPropertyValue(name).trim() || fallback);
    const accent = token("--accent", "#4A3F5C");
    const info = token("--info", "#4D617A");
    const muted = token("--muted", "#6B675D");
    const border = token("--border", "#DDD8CE");

    const data = [xs, targetCumul, realCumul];
    const opts = {
      width,
      height: 260,
      padding: [12, 8, 4, 8],
      scales: { x: { time: false } },
      axes: [
        {
          values: (_u, splits) => splits.map((v) => MONTH_LABELS[v - 1] ?? ""),
          stroke: muted,
          grid: { show: false },
          ticks: { show: false },
        },
        {
          values: (_u, splits) => splits.map((v) => formatEurShort(v)),
          stroke: muted,
          grid: { stroke: border, width: 1 },
          ticks: { show: false },
        },
      ],
      series: [
        {},
        { label: "Objectif cumulé", stroke: accent, width: 2, points: { show: false } },
        { label: "CA HT encaissé cumulé", stroke: info, width: 2, points: { show: false } },
      ],
    };

    container.replaceChildren();
    this._chart = new uPlot(opts, data, container);

    if (!this._chartResizeBound) {
      this._chartResizeBound = true;
      window.addEventListener("resize", () => {
        if (this._chart && this.hasContainer()) {
          this._chart.setSize({ width: this.element.clientWidth, height: 260 });
        }
      });
    }
  }

  hasContainer() {
    return this.element instanceof HTMLElement;
  }
}

function formatEurShort(value) {
  if (!Number.isFinite(value)) return "";
  if (value >= 1000) return `${Math.round(value / 1000)} k€`;
  return `${Math.round(value)} €`;
}
